import { useRef, useState } from "react";
import { Volume2, Mic, Send, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function Expression({ story, narration }) {
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState(null);
  const recRef = useRef(null);

  const dictate = () => {
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.onresult = (e) => setAnswer((a) => `${a} ${e.results[0][0].transcript}`.trim());
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/expression/feedback", { story_id: story.id, answer });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 md:grid-cols-5">
      <div className="rise card-paper p-6 md:col-span-3">
        <div className="flex items-start gap-3">
          <h2 data-testid="expression-prompt" className="font-display text-2xl font-bold sm:text-3xl">{story.expression_prompt}</h2>
          <button data-testid="expression-listen-button" onClick={() => narration.play(story.expression_prompt)} className="rounded-full bg-[color:var(--paper-2)] p-2.5 hover:bg-[color:var(--sun)]"><Volume2 size={20} /></button>
        </div>
        <textarea data-testid="expression-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)} rows={5} maxLength={1000}
          placeholder="Écris ou dicte ta réponse ici…"
          className="mt-5 w-full rounded-2xl border-2 border-[color:var(--line)] p-4 font-story text-xl outline-none focus:border-[color:var(--terracotta)]" />
        <div className="mt-4 flex flex-wrap gap-3">
          {SR && <button data-testid="expression-dictate-button" onClick={dictate} disabled={listening} className={`btn-ghost ${listening ? "pulse-ring" : ""}`}><Mic size={18} /> {listening ? "Je t'écoute…" : "Dicter"}</button>}
          <button data-testid="expression-submit-button" onClick={send} disabled={busy || answer.trim().length < 2} className="btn-primary">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Envoyer
          </button>
        </div>
      </div>
      <div className="md:col-span-2">
        {result ? (
          <div data-testid="expression-feedback" className="rise card-paper space-y-4 bg-[#FFF6E3] p-6">
            <div className="flex gap-1">{[1, 2, 3].map((k) => <Star key={k} size={30} className={k <= result.stars ? "fill-[color:var(--sun)] text-[color:var(--sun)]" : "text-[color:var(--line)]"} />)}</div>
            <p className="text-lg font-semibold">{result.feedback}</p>
            {result.better_sentence && (
              <div>
                <p className="text-sm font-extrabold uppercase tracking-wider text-[color:var(--sage)]">On peut aussi dire</p>
                <p className="font-story text-xl italic">« {result.better_sentence} »</p>
              </div>
            )}
            {result.new_words?.length > 0 && (
              <div className="flex flex-wrap gap-2">{result.new_words.map((w) => <span key={w} className="rounded-full bg-white px-3 py-1 font-bold text-[color:var(--terracotta)]">{w}</span>)}</div>
            )}
          </div>
        ) : (
          <div className="card-paper grid h-full min-h-48 place-items-center p-6 text-center text-[color:var(--muted-ink)]">
            <p className="font-display text-xl">Raconte avec tes mots, une maîtresse magique te répondra !</p>
          </div>
        )}
      </div>
    </div>
  );
}
